/** Right-click (or long-press, Shift+F10) menu on the main rail: choose icons with or without text,
 *  and on an item hide it, or open the customise dialog. Empty rail space gets the same menu minus Hide. */

import React from 'react';
import { CheckIcon } from '@/shared/ui/icons';

import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuRadioGroup,
  ContextMenuRadioItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '../../shared/ui/context-menu';
import { cn } from '../../shared/ui/utils';
import { sanitiseLabelMode, type MainRailLabelMode } from './useMainRailLabelMode';
import { MAIN_RAIL_PINNED_IDS } from './useMainRailPreferences';

export type MainRailMenuActions = {
  onHide: (id: string) => void;
  onCustomise: () => void;
  labelMode: MainRailLabelMode;
  onLabelModeChange: (mode: MainRailLabelMode) => void;
  /** Touch tablets always show icons only, so the choice is not offered there. */
  labelModeLocked?: boolean;
};

const MODE_OPTIONS: { value: MainRailLabelMode; label: string }[] = [
  { value: 'icons-and-text', label: 'Icons and text' },
  { value: 'icons-only', label: 'Icons only' },
];

// Slack's menu metrics: 200px wide, 28px rows, 15px text inset 24px, full-bleed hover.
const MENU_CLASS = 'w-[200px] min-w-[200px] rounded-[10px] px-0 py-3';
const ITEM_CLASS = 'h-7 gap-0 rounded-none px-6 py-0 !text-[15px] !font-normal';

function ShowGroup({ actions }: { actions: MainRailMenuActions }) {
  if (actions.labelModeLocked) return null;
  return (
    <>
      <ContextMenuLabel className="px-6 py-1 !text-[13px] !font-normal text-muted-foreground">Show</ContextMenuLabel>
      <ContextMenuRadioGroup
        value={actions.labelMode}
        onValueChange={(value) => actions.onLabelModeChange(sanitiseLabelMode(value))}
      >
        {MODE_OPTIONS.map((option) => (
          <ContextMenuRadioItem
            key={option.value}
            value={option.value}
            className={cn(
              // The shared radio draws a dot; this menu wants a check and the accent colour.
              ITEM_CLASS,
              '[&>span:first-child]:hidden data-[state=checked]:text-primary',
            )}
          >
            <span className="absolute left-1 flex size-5 items-center justify-center" aria-hidden="true">
              {actions.labelMode === option.value ? <CheckIcon className="size-4" /> : null}
            </span>
            {option.label}
          </ContextMenuRadioItem>
        ))}
      </ContextMenuRadioGroup>
      <ContextMenuSeparator className="mx-0" />
    </>
  );
}

export function MainRailContextMenu({
  itemId,
  label,
  actions,
  children,
}: {
  itemId: string;
  label: string;
  actions: MainRailMenuActions;
  children: React.ReactNode;
}) {
  const pinned = MAIN_RAIL_PINNED_IDS.includes(itemId);
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent data-slot="main-rail-context-menu" className={MENU_CLASS}>
        <ShowGroup actions={actions} />
        <ContextMenuItem className={ITEM_CLASS} disabled={pinned} onSelect={() => actions.onHide(itemId)}>
          Hide {label}
        </ContextMenuItem>
        <ContextMenuItem className={ITEM_CLASS} onSelect={() => actions.onCustomise()}>Customise rail...</ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

export function MainRailEmptyContextMenu({
  actions,
  children,
}: {
  actions: MainRailMenuActions;
  children: React.ReactNode;
}) {
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent data-slot="main-rail-context-menu" className={MENU_CLASS}>
        <ShowGroup actions={actions} />
        <ContextMenuItem className={ITEM_CLASS} onSelect={() => actions.onCustomise()}>Customise rail...</ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

export function MainRailEmptyMenuIf({
  enabled,
  actions,
  children,
}: {
  enabled: boolean;
  actions: MainRailMenuActions;
  children: React.ReactElement;
}) {
  return enabled ? <MainRailEmptyContextMenu actions={actions}>{children}</MainRailEmptyContextMenu> : children;
}
